import { useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, ArrowRight, Download, FileText, CheckCircle2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { api, API } from "@/lib/api";

/**
 * Gated lead magnet dialog for the AI Workflow Automation Playbook PDF.
 * Posts the lead to /api/lead-magnet, triggers a Resend email with the PDF link,
 * and reveals the direct download URL on success.
 */
export default function PlaybookDialog({ trigger, source = "ai_playbook" }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { download_url }
  const [open, setOpen] = useState(false);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const res = await api.post("/lead-magnet", { ...data, source });
      setSuccess({ download_url: res.data.download_url || `${API}/playbook` });
      toast.success("Playbook on its way to your inbox.");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setSuccess(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="playbook-dialog"
        className="sm:max-w-md bg-[#0B2138] border border-white/10 text-white"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/30 text-cyan">
              <BookOpen className="h-4 w-4" />
            </div>
            <span className="overline">Free Playbook</span>
          </div>
          <DialogTitle className="font-display text-xl font-semibold text-white">
            The AI Workflow Automation Playbook
          </DialogTitle>
          <DialogDescription className="text-ink-400 text-sm">
            The patterns we use to design, build and operate LLM-powered workflows in production — pilot framework, anti-patterns, recommended stack.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="mt-3 space-y-4" data-testid="playbook-success">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <CheckCircle2 className="h-4 w-4" /> Your playbook is on the way to your inbox.
            </div>
            <a
              href={success.download_url}
              target="_blank"
              rel="noreferrer"
              className="btn-primary w-full"
              data-testid="playbook-direct-download"
            >
              <Download className="h-4 w-4" /> Download the PDF now
            </a>
            <p className="text-[11px] text-ink-400">
              You can also grab it directly: <span className="text-cyan">{success.download_url}</span>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-3 space-y-3" data-testid="playbook-form">
            <div>
              <label className="text-xs text-ink-200">Full name</label>
              <input
                {...register("name", { required: "Required" })}
                className="mt-1.5 w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition"
                placeholder="Your name"
                data-testid="playbook-name"
              />
              {errors.name && <span className="mt-1 block text-[11px] text-red-300">{errors.name.message}</span>}
            </div>
            <div>
              <label className="text-xs text-ink-200">Work email</label>
              <input
                type="email"
                {...register("email", { required: "Required" })}
                className="mt-1.5 w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition"
                placeholder="you@company.com"
                data-testid="playbook-email"
              />
              {errors.email && <span className="mt-1 block text-[11px] text-red-300">{errors.email.message}</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-ink-200">Company</label>
                <input
                  {...register("company")}
                  className="mt-1.5 w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition"
                  placeholder="Optional"
                  data-testid="playbook-company"
                />
              </div>
              <div>
                <label className="text-xs text-ink-200">Role</label>
                <input
                  {...register("role_title")}
                  className="mt-1.5 w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition"
                  placeholder="CTO, Head of AI…"
                  data-testid="playbook-role"
                />
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full mt-2" data-testid="playbook-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4" /> Send me the playbook <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="text-[11px] text-ink-400">
              We&apos;ll email the PDF and may occasionally share related insights. No spam.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
